<script>
  import { tick, untrack } from 'svelte'
  import { flip } from 'svelte/animate'
  import { dndzone } from 'svelte-dnd-action'
  import EditableTitle from './EditableTitle.svelte'
  import { reduceMotion, flipDurationMs } from '../lib/motion.js'

  let {
    neurons = [],
    parentId = null,
    onTransition = () => {},
    onNavigate = () => {},
    onReorder = () => {},
    onSaveTitle = () => {},
    onShowSaved = () => {},
    countdowns = {},
    fadingOut = {},
    slashCommands = [],
  } = $props()

  const COLUMNS = [
    { phase: 'draft', label: 'Draft' },
    { phase: 'todo', label: 'To-do' },
    { phase: 'in-progress', label: 'In progress' },
    { phase: 'done', label: 'Done' },
  ]

  const PHASES = COLUMNS.map(c => c.phase)
  const emptyColumns = () => Object.fromEntries(PHASES.map(p => [p, []]))
  let columnItems = $state(emptyColumns())
  let boardEl = $state(null)
  let dragging = $state(false)
  let preDragCounts = {}

  $effect(() => {
    if (dragging) return
    const grouped = emptyColumns()
    for (const n of neurons) {
      const phase = n.doing_phase || 'draft'
      if (grouped[phase]) {
        grouped[phase].push({ ...n })
      }
    }
    const currentColumns = untrack(() => columnItems)
    const merged = {}
    for (const phase of PHASES) {
      const existing = currentColumns[phase]
      const incoming = grouped[phase]
      const incomingMap = new Map(incoming.map(n => [n.id, n]))
      const existingIds = new Set(existing.map(n => n.id))
      const kept = existing
        .filter(n => incomingMap.has(n.id))
        .map(n => incomingMap.get(n.id))
      const added = incoming.filter(n => !existingIds.has(n.id))
      merged[phase] = [...added, ...kept]
    }
    columnItems = merged
  })

  function handleConsider(phase, e) {
    if (!dragging) {
      preDragCounts = Object.fromEntries(PHASES.map(p => [p, columnItems[p].length]))
    }
    dragging = true
    columnItems = { ...columnItems, [phase]: e.detail.items }
  }

  async function handleFinalize(phase, e) {
    const prevHeight = !reduceMotion && boardEl
      ? boardEl.getBoundingClientRect().height
      : null

    const newItems = e.detail.items
    const itemsLeft = newItems.length < (preDragCounts[phase] || 0)
    columnItems = { ...columnItems, [phase]: newItems }

    if (prevHeight !== null) {
      await tick()
      const newHeight = boardEl.getBoundingClientRect().height
      if (Math.abs(newHeight - prevHeight) > 2) {
        boardEl.style.overflow = 'hidden'
        boardEl.style.transition = 'none'
        boardEl.style.height = `${prevHeight}px`
        boardEl.offsetHeight
        boardEl.style.transition = 'height 250ms cubic-bezier(0.32, 0.72, 0, 1)'
        boardEl.style.height = `${newHeight}px`
        boardEl.addEventListener('transitionend', function handler() {
          boardEl.style.height = ''
          boardEl.style.overflow = ''
          boardEl.style.transition = ''
          boardEl.removeEventListener('transitionend', handler)
        })
      }
    }

    if (itemsLeft) return

    for (const item of newItems) {
      if (item.doing_lifecycle !== 'kanban') {
        await onTransition(item.id, phase, 'kanban')
        await onReorder(parentId, newItems.map(n => n.id))
        dragging = false
        return
      }
      if (item.doing_phase !== phase) {
        await onTransition(item.id, phase)
        await onReorder(parentId, newItems.map(n => n.id))
        dragging = false
        return
      }
    }

    dragging = false

    if (newItems.length > 1) {
      await onReorder(parentId, newItems.map(n => n.id))
    }
  }
</script>

<div data-testid="kanban-board" class="kanban-breakout" bind:this={boardEl}>
  <div class="kanban-columns">
    {#each COLUMNS as col}
      <div class="flex flex-col gap-2" data-testid="kanban-column-{col.phase}">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-text-muted px-2 py-1">
          {col.label}
        </h3>
        <div
          class="flex flex-1 flex-col gap-2 min-h-[4rem] rounded-lg p-2 bg-surface-2/50"
          use:dndzone={{ items: columnItems[col.phase], flipDurationMs, type: 'neuron', dropTargetStyle: {}, dragHandleSelector: '[data-drag-handle]', delayTouchStart: 200 }}
          onconsider={(e) => handleConsider(col.phase, e)}
          onfinalize={(e) => handleFinalize(col.phase, e)}
        >
          {#each columnItems[col.phase] as neuron (neuron.id)}
            <div
              animate:flip={{ duration: flipDurationMs }}
              data-neuron-id={neuron.id}
              class="flex items-center gap-2 rounded-md px-3 py-2 bg-surface-0 border border-border"
              class:fading-out={fadingOut[neuron.id]}
            >
              <span
                data-drag-handle
                aria-label="Drag to reorder"
                class="select-none text-lg leading-none min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted opacity-40 hover:opacity-70 cursor-grab active:cursor-grabbing"
              >⠿</span>

              <EditableTitle
                title={neuron.title}
                onSave={(newValue) => onSaveTitle(neuron.id, newValue)}
                onSaved={() => onShowSaved(neuron.id)}
                testId="neuron-title"
                class="flex-1 text-base"
                {slashCommands}
                dblClickToEdit={true}
              />

              {#if countdowns[neuron.id] != null}
                <span
                  data-testid="countdown"
                  class="shrink-0 text-xs tabular-nums text-text-muted"
                >{countdowns[neuron.id]}s</span>
              {:else}
                <button
                  data-testid="navigate-arrow"
                  onclick={() => onNavigate(neuron)}
                  class="shrink-0 flex items-center justify-center gap-0.5 min-w-[44px] min-h-[44px] text-text-muted opacity-40 hover:opacity-90 transition-opacity cursor-pointer"
                  aria-label="Navigate into {neuron.title}"
                >
                  {#if neuron.children_count > 0}
                    <span class="text-xs tabular-nums">{neuron.children_count}</span>
                  {/if}
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M7 4l6 6-6 6"/>
                  </svg>
                </button>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  .kanban-breakout {
    margin-left: -1rem;
    margin-right: -1rem;
    overflow-x: clip;
  }

  @media (min-width: 640px) {
    .kanban-breakout {
      width: 100dvw;
      position: relative;
      left: 50%;
      margin-left: -50dvw;
    }
  }

  .kanban-columns {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.75rem;
    padding: 0.5rem 1rem;
  }

  @media (max-width: 640px) {
    .kanban-columns {
      grid-template-columns: repeat(4, 70vw);
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      padding: 0.5rem 1rem;
      overscroll-behavior-x: contain;
    }

    .kanban-columns > :global(div) {
      scroll-snap-align: center;
    }
  }
</style>
